import React from 'react';
import { Layout, Cell, Text, Heading } from 'wix-style-react';
import { object, func } from 'prop-types';
import Phone from 'wix-ui-icons-common/Phone';
import Email from 'wix-ui-icons-common/Email';
import Location from 'wix-ui-icons-common/Location';
import Time from 'wix-ui-icons-common/Time';
import { CustomerInfoGMap } from '../Maps/CustomerInfoGMap';
import { translate } from 'react-i18next';
import Avatar from './Avatar';
import styles from './OrderHistoryCustomer.scss';

export function Divider() {
  return (
    <Cell span={12}>
      <div className={styles.divider} />
    </Cell>
  );
}

const mapStyle = {
  backgroundColor: '#9acde5',
  display: 'block',
  height: 180,
  borderRadius: 6,
};

function Map(props) {
  const { fullName, orderAddress, restaurantAddress } = props;
  return (
    <div data-hook={props.dataHook}>
      <div style={mapStyle}>
        <CustomerInfoGMap
          restaurantAddress={restaurantAddress}
          customerLocation={orderAddress && orderAddress.latLng}
        />
      </div>
      <div style={{ zIndex: '1000', position: 'relative' }}>
        <Avatar dataHook="order-history-customer-avatar" className={styles.avatar} />
      </div>
      <Heading data-hook="order-history-customer-name" appearance="H2" className={styles.fullName}>
        {fullName}
      </Heading>
    </div>
  );
}

function getIcon(icon) {
  switch (icon) {
    case 'phoneOutline':
      return <Phone />;
    case 'locationOutline':
      return <Location />;
    case 'clockOutline':
      return <Time />;
    case 'mailOutline':
      return <Email />;
    default:
      return null;
  }
}

function LineDetail({ title, text, t }) {
  return (
    <div className={styles.detail} key={title}>
      <Text size="small" weight="normal" secondary>
        {t(title)} {t(text)}
      </Text>
    </div>
  );
}

function PartsDetail({ parts, t }) {
  const strings = parts.map((part) => {
    return `${t(part.title)} ${part.text}`;
  });

  return (
    <div className={styles.detail}>
      <Text size="small" weight="normal" secondary>
        {strings.join(' / ')}
      </Text>
    </div>
  );
}

function IconDetail({ icon, link, ariaLabel, text, t }) {
  const str = t(text);
  return (
    <div className={styles.iconDetail}>
      <div className={styles.detailIcon}>{getIcon(icon)}</div>
      <Text dataHook={`order-history-customer-${icon}`} size="medium" weight="normal">
        {link ? (
          <a href={link} title={t(ariaLabel)}>
            {str}
          </a>
        ) : (
          str
        )}
      </Text>
    </div>
  );
}

function Payments({ title, payments, t }) {
  return (
    <Layout gap={'6'}>
      <Cell span={12}>
        <Text size="small" weight="normal" secondary>
          {t(title)}
        </Text>
      </Cell>
      {payments.map((payment) => {
        return (
          <React.Fragment>
            <Cell span={10}>
              <Text dataHook="order-history-customer-details-payment-title" size="medium" weight="normal">
                {t(payment.title)}
              </Text>
            </Cell>
            <Cell span={2}>
              <Text
                dataHook="order-history-customer-details-payment-amount"
                className={styles.paymentAmount}
                size="medium"
                weight="normal"
                tagName="div"
              >
                {t(payment.amount)}
              </Text>
            </Cell>
          </React.Fragment>
        );
      })}
    </Layout>
  );
}

function renderDetail(detail, t) {
  if (detail.type === 'icon') {
    return <IconDetail {...detail} t={t} />;
  } else if (detail.type === 'line') {
    return <LineDetail {...detail} t={t} />;
  } else if (detail.type === 'parts') {
    return <PartsDetail {...detail} t={t} />;
  }
}

function OrderHistoryCustomer({ displayableOrder, orderAddress, restaurantAddress, t }) {
  const shouldShowCustomerLocation = displayableOrder.deliveryType !== 'takeout';
  return (
    <div className={styles.wrapper}>
      <Map
        dataHook="order-history-customer-map"
        user={displayableOrder}
        restaurantAddress={restaurantAddress}
        orderAddress={shouldShowCustomerLocation ? orderAddress : undefined}
        fullName={displayableOrder.user.fullName}
      />

      <div className={styles.customerDetails}>{displayableOrder.details.map((detail) => renderDetail(detail, t))}</div>

      <Divider />

      <Payments data-hook="order-history-customer-payments" {...displayableOrder.payments} t={t} />
    </div>
  );
}

OrderHistoryCustomer.displayName = 'OrderHistoryCustomer';
OrderHistoryCustomer.propTypes = {
  displayableOrder: object,
  restaurantAddress: object,
  orderAddress: object,
  t: func,
};

export default translate()(OrderHistoryCustomer);
